"use client";

import { Component, type ReactNode } from "react";
import * as Sentry from "@sentry/nextjs";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export default class GlobeErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log to console for Fly.io log drain visibility
    console.error("GlobeErrorBoundary caught error:", error.message, {
      componentStack: info.componentStack,
    });
    // Report to Sentry with component stack for root-cause analysis
    Sentry.captureException(error, {
      extra: { componentStack: info.componentStack },
      tags: { component: "GlobeVisualization" },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <div className="text-center space-y-3 max-w-md px-6">
            <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center" style={{ background: "rgba(244,63,94,0.1)", border: "1px solid rgba(244,63,94,0.2)" }}>
              <svg className="w-7 h-7 text-[#F43F5E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-[#F4F4F5]">Globe failed to load</h2>
            <p className="text-sm text-[#71717A] leading-relaxed">
              The 3D visualization encountered an error. This usually happens due to WebGL compatibility issues.
            </p>
            <button
              onClick={() => this.setState({ hasError: false })}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-[#27272A] text-[#F4F4F5] border border-[rgba(255,255,255,0.08)] hover:bg-[#3f3f46] transition-colors cursor-pointer"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
